import {Text} from '@/components-v2/atoms'
import {CurrencyType, PromotionType} from '@/types/promotion'
import {PromotionContionsTabList} from '@/utils/constants/promotionConstants'
import {useMemo} from 'react'

/**
 * usePromotionReview
 * Extracts and memoizes the logic for generating promotion preview strings.
 * @param {object} params
 * @param {Array} params.conditions - Promotion conditions array
 * @param {number} params.activePromoCondition - Active tab index
 * @param {string} params.countryCode - Country code for currency
 * @param {string} params.promotionType - Type of the promotion
 * @param {import('react').ComponentType<any>} [params.TextComponent=Text] - Text component used by the caller
 * @returns {object} { discountStrings, createPreviewMessage, isMoreBtnVisible }
 */
export function usePromotionReview({
  conditions = [],
  activePromoCondition = 0,
  countryCode,
  promotionType,
  TextComponent = Text,
}) {
  const currencyCode = CurrencyType[countryCode ?? 'MX'] ?? '$'
  const selectedUom = PromotionContionsTabList[activePromoCondition]?.uom ?? 'EA'
  const unitLabel = PromotionContionsTabList[activePromoCondition]?.tabLabel.toLowerCase() ?? 'items'
  const isCombinedProduct = conditions?.[0]?.conditionGroup?.conditions?.[0]?.productCombination

  const formatValue = (type, value) => (type === 'AMOUNT' ? `${currencyCode}${value}` : `${value}%`)

  const formatSingleProductByType = (type, value, quantity, isMultiple = false, secondQuantity) => {
    const formattedValue = formatValue(type, value)
    const productText = isMultiple
      ? `of products from Product List X and ${secondQuantity} ${unitLabel} of products from Product List Y each`
      : `of any product${isCombinedProduct ? ' combination' : ''} from Product List X`

    if (type === 'POINTS') {
      return `Get ${value} points when buying ${quantity} ${unitLabel} ${productText}`
    }
    if (type === 'FREE_PRODUCT') {
      return `Get ${value} Free Product(s) when buying ${quantity} ${unitLabel} ${productText}`
    }
    if (type === 'GIFT_ITEM') {
      return `Get ${value} Gift Items when buying ${quantity} ${unitLabel} ${productText}`
    }

    return `Get ${formattedValue} discount when buying ${quantity} ${unitLabel} ${productText}`
  }

  const formatStoreOrWelcomeDiscount = (type, value, maxAmount) => {
    const formattedValue = formatValue(type, value)
    const maxPart = maxAmount || maxAmount === 0 ? ` (max: ${currencyCode}${maxAmount})` : ''

    if (promotionType === PromotionType.WELCOME_DISCOUNT) {
      return `Get ${formattedValue}${maxPart} welcome discount after the first order.`
    }
    return `Get ${formattedValue}${maxPart} as store discount`
  }

  const getDiscountStrings = (conditions, selectedUom) => {
    if (promotionType === PromotionType.STORE_DISCOUNT || promotionType === PromotionType.WELCOME_DISCOUNT) {
      return conditions.flatMap(cond => {
        if (!cond?.rewards?.length) return []
        return cond.rewards
          .filter(reward => reward?.type === 'DISCOUNT')
          .map(reward =>
            formatStoreOrWelcomeDiscount(reward.discountType, reward?.discountValue ?? 0, reward?.discountAmountUpto)
          )
      })
    }

    return conditions.flatMap(cond => {
      if (!cond?.rewards?.length) return []

      const groupConditions = cond?.conditionGroup?.conditions ?? []
      if (!groupConditions.length) return []

      const isMultipleProducts = promotionType === PromotionType.BUY_MULTIPLE_PRODUCTS && groupConditions.length > 1
      const firstCondition = groupConditions[0]
      const secondCondition = groupConditions[1]
      const quantity = selectedUom === 'EA' ? (firstCondition?.amount ?? 0) : (firstCondition?.quantity ?? 0)
      const secondQuantity = selectedUom === 'EA' ? (secondCondition?.amount ?? 0) : (secondCondition?.quantity ?? 0)

      return cond.rewards
        .map(reward => {
          if (!reward?.discountType && !reward?.type) return null
          const value = reward?.discountValue ?? reward?.points ?? reward?.quantity ?? 0
          const type = reward.discountType ? reward.discountType : reward.type
          return formatSingleProductByType(type, value, quantity, isMultipleProducts, secondQuantity)
        })
        .filter(Boolean)
    })
  }

  const discountStrings = useMemo(
    () => getDiscountStrings(conditions, selectedUom),
    [conditions, selectedUom, promotionType, unitLabel, isCombinedProduct, currencyCode]
  )
  const isMoreBtnVisible = discountStrings.length > 5

  const renderPreviewText = text =>
    text.split(/(Product List X|Product List Y)/g).map((part, index) =>
      part === 'Product List X' || part === 'Product List Y' ? (
        <TextComponent
          key={index}
          style={{color: '#005cbc', fontStyle: 'italic'}}
        >
          {part}
        </TextComponent>
      ) : (
        part
      )
    )

  const createPreviewMessage = (isVisibleAll = false, isModal=false) => {
    const previewList = !isVisibleAll ? discountStrings.slice(0, 5) : discountStrings
    const isLayered = previewList.length > 1
    return (
      <div className="flex flex-col">
        {previewList?.map((str, index) => (
          <TextComponent key={index}>
            {isLayered && <TextComponent variant="bold">{`Layer ${index + 1}: `}</TextComponent>}
            {isModal ? str : renderPreviewText(str)}
            <br />
          </TextComponent>
        ))}
      </div>
    )
  }

  return {discountStrings, createPreviewMessage, isMoreBtnVisible}
}
